import { type NaicsCodeId, NaicsCodes, type SuretyAccountId } from "@inrev/common";
import {
	useFetchAccountSpecialtyCodes,
	useUpdateAccountNaicsCodes,
} from "@portal/api/account/agent";
import { AccountSpecialtyField } from "@portal/components/features/account/AccountSpecialtyField";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Spinner } from "@portal/components/primitives";
import { MutationErrorFlag } from "@portal/components/ui/MutationErrorFlag";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

type AccountSpecialtySectionProps = {
	accountId: SuretyAccountId;
	readOnly?: boolean;
};

export const AccountSpecialtySection: FC<AccountSpecialtySectionProps> = ({
	accountId,
	readOnly = false,
}) => {
	const {
		rows: specialtyRows,
		isLoading: specialtyIsLoading,
		isFetching: specialtyIsFetching,
		error: specialtyError,
		refetch: refetchSpecialtyCodes,
	} = useFetchAccountSpecialtyCodes(accountId);
	const { updateAccountNaicsCodes, updateAccountNaicsCodesError } =
		useUpdateAccountNaicsCodes(accountId);
	const specialtyIds = useMemo(() => specialtyRows.map((row) => row.id), [specialtyRows]);
	const [naicsDraft, setNaicsDraft] = useState<NaicsCodeId[]>(specialtyIds);
	const [naicsPickerOpen, setNaicsPickerOpen] = useState(false);
	const naicsPickerOpenRef = useRef(naicsPickerOpen);
	naicsPickerOpenRef.current = naicsPickerOpen;

	useEffect(() => {
		if (naicsPickerOpenRef.current) return;
		if (specialtyIsLoading) return;
		setNaicsDraft((draft) => (NaicsCodes.areSame(draft, specialtyIds) ? draft : specialtyIds));
	}, [specialtyIds, specialtyIsLoading]);

	const persistNaics = useCallback(() => {
		if (NaicsCodes.areSame(naicsDraft, specialtyIds)) return;
		updateAccountNaicsCodes({ naicsCodeIds: naicsDraft });
	}, [naicsDraft, specialtyIds, updateAccountNaicsCodes]);

	if (specialtyIsLoading) {
		return <Spinner size="lg" />;
	}

	if (specialtyError) {
		return (
			<ReportedRetryableLoadError
				error={specialtyError}
				resource="account specialty"
				onRetry={refetchSpecialtyCodes}
				retrying={specialtyIsFetching}
				testId="agent-account-specialty-load-error"
			/>
		);
	}

	return (
		<div data-testid="agent-account-specialty-section">
			<AccountSpecialtyField
				testId="agent-account-specialty"
				value={naicsDraft}
				onChange={setNaicsDraft}
				onBlur={persistNaics}
				open={naicsPickerOpen}
				onOpenChange={setNaicsPickerOpen}
				readOnly={readOnly}
			/>
			<MutationErrorFlag
				errorHeading="Unable to update specialty"
				mutationError={updateAccountNaicsCodesError}
			/>
		</div>
	);
};
